import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Box, CircularProgress, Paper, Typography, useTheme } from '@mui/material';
import { componentLogger } from '@ragu-components/logger';
import { canAccess } from '@ratto-components/ra-rbac';
import {
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  useGetOne,
  useNotify,
  usePermissions,
  useTranslate,
} from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';

import type { AutoprovisioningSettings, AutoprovisioningSettingsUpdate } from '@ragu-components/ord-types';
import type { FieldValues } from 'react-hook-form';

export const ApSettingsPage = (): React.ReactElement => {
  const dataProvider = useIngordDataProvider();
  const { permissions } = usePermissions();
  const canList = canAccess({ permissions, action: 'list', resource: 'ap_settings' });
  const canCreate = canAccess({ permissions, action: 'create', resource: 'ap_settings' });
  const notify = useNotify();
  const translate = useTranslate();
  const theme = useTheme();
  const [formKey, setFormKey] = useState(0);

  const { data, isLoading, error, refetch } = useGetOne<AutoprovisioningSettings & { id: string }>(
    'autoprovisioning/settings',
    { id: '' },
    { enabled: canList }
  );

  useEffect(() => {
    if (error) {
      componentLogger.error('Autoprovisioning settings fetch failed', { error });
      notify('resources.autoprovisioning.settings.notifications.fetchError', { type: 'error' });
    }
  }, [error, notify]);

  const handleSave = useCallback(
    async (values: FieldValues) => {
      const { id: _id, ...formData } = values;
      try {
        await dataProvider.updateAutoprovisioningSettings(formData as AutoprovisioningSettingsUpdate);
        await refetch();
        setFormKey((k) => k + 1);
        componentLogger.info('Autoprovisioning settings updated', { values });
        notify('resources.autoprovisioning.settings.notifications.saveSuccess', { type: 'info' });
      } catch (err) {
        componentLogger.error('Autoprovisioning settings update failed', { error: err });
        notify('resources.autoprovisioning.settings.notifications.saveError', { type: 'error' });
      }
    },
    [dataProvider, refetch, notify]
  );

  if (isLoading && canList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Paper sx={{ mt: theme.spacing(1.5), overflow: 'hidden' }}>
      <Box sx={{ px: 2, pt: 2 }}>
        <Typography variant='h6'>{translate('resources.autoprovisioning.settings.name')}</Typography>
      </Box>
      <SimpleForm
        key={formKey}
        onSubmit={handleSave}
        defaultValues={data}
        toolbar={
          canCreate ? (
            <Toolbar>
              <SaveButton label={translate('ra.action.save')} />
            </Toolbar>
          ) : (
            false
          )
        }
      >
        <TextInput
          source='outbound_proxy'
          label='resources.autoprovisioning.settings.fields.outbound_proxy'
          fullWidth
        />
        <TextInput
          source='outbound_backup_proxy'
          label='resources.autoprovisioning.settings.fields.outbound_backup_proxy'
          fullWidth
        />
      </SimpleForm>
    </Paper>
  );
};
